<template>
    <div class="request-card">
        <div class="request-card-header" v-if="!noFilter">
            <Tabs />
            <Button 
                v-b-tooltip.left 
                title="Filter"
                @click.native = "toggleFilter"
                id="filter-dashboard">
                    <Icon icon="dashboard-filter-icon" />
            </Button>
        </div>
        <Filters v-show="showFilter" :data="$route.query.id ? data : null"/>
        <div class="request-card-body">
            <Table v-if="data.length" :columns="columns" :data="data" :set-expanded-data = "setExpandedData" :loading="loading">
                <template v-slot:MyCampaign="{ row }">
                    <Campaign :data="tabSection === 'sent' ? row.sender : row.receiver"/>
                </template>
                <template v-slot:PartnerCampaign="{ row }">
                    <Campaign :data="tabSection === 'sent' ? row.receiver : row.sender" />
                </template>
                <template v-slot:UpdateCampaign="{ row }">
                    <Campaign :data="row.campaign" :truncate-length="120"/>
                </template>
                <template v-slot:SuggestedSpots="{ row }">
                    <SuggestedSpots :data="row"/>
                </template>
                <template v-slot:Status="{ row }">
                    <Status :status="row.status"/>
                </template>
                <template v-slot:UpdatesStatus="{ row }">
                    <UpdatesStatus :spots="row.spots"/>
                </template>
                <template v-slot:UpdateSpots="{ row }">
                    <UpdateSpots :spots="row.spots"/>
                </template>
                <template v-slot:Chat="{ row }">
                    <ChatIcon :request = "row" @setRequestData = "setExpandedData"/>
                </template>
                <template v-slot:Action="{ row, expanded }">
                    <Actions 
                        @setRequestData="setExpandedData" 
                        v-if="row.status === 'pending' && !expanded" 
                        :request-data="row"
                        :requestsList = "data"
                    />
                </template>
                <template v-slot:Date="{ row }">
                    <Date :data="row.created_date || row.date"/>
                </template>
                <template v-slot:NotScheduledUpdates>
                    <i class="warning">No Updates scheduled yet</i>
                </template>
                <template v-slot:ScheduleUpdate = "{ row }">
                    <ScheduleNow :campaign="row"/>
                </template>
                <!-- <template v-slot:SaveToKickstarter = "{ row }" style="text-align: right">
                </template> -->
                <template v-slot:CpInfo = "{ row }" style="text-align: right">
                    <div class="d-flex justify-content-end">
                        <SaveToKickstarter v-if="row.spots && row.spots.some(el => el && el.uid)" :data = "row"/>
                        <CpInfo v-if="row.spots && row.spots.some(el => el && el.uid)" :data = "row"/>
                    </div>
                </template>
                <template v-slot:InvoiceDates = "{row}">
                    <InvoiceDates :data="row" />
                </template>
                <template v-slot:InvoiceStatus = "{row}">
                    <InvoiceStatus :data="row" />
                </template>
                <template v-slot:InvoiceFile = "{row}">
                    <InvoiceFile :url="row.invoice_pdf" />
                </template>
                <template v-slot:Description>
                    <Description  />
                </template>
                <template v-slot:Amount = "{row}">
                    <Amount :amountPaid="row.amount_paid" />
                </template>
            </Table>
            <Loader 
                v-if = "!data.length && loading" />
            <EmptyState 
                v-if = "!data.length && !loading && emptyState.icon"
                :emptyState="emptyState"/>
        </div>
        <div class="request-card-footer" v-if="!noFooter">
            <RowCount v-if = "data.length && totalCount > DEFAULT_PER_PAGE"/>
            <Pagination 
                v-if = "data.length && (totalCount > ($route.query.perPage || DEFAULT_PER_PAGE))"
                :total-count="totalCount"
            />
        </div>
    </div>
</template>

<script>
    import {
        computed, onMounted, ref, watch
    } from '@vue/composition-api'
    import { EMPTY_STATES, DEFAULT_PER_PAGE } from '@/core/utils/dashboard.utils'
    import {useRouter} from '../../../../core/hooks'
    import Table from './Table'
    import Icon from '@/components/Theme/Icon'
    import RowCount from '../RowCount'
    import Pagination from './Pagination'
    import EmptyState from '../EmptyStates'
    import useEmptyState from '../EmptyStates/useEmptyState'
    import Button from '../Button'
    import Filters from '../../components/Card/Filters/index'
    import Loader from '../../../../components/Icons/Loader'

    import {
        Actions,
        Campaign,
        ChatIcon,
        Date,
        Spot,
        Status,
        UpdatesStatus,
        UpdateSpots,
        SuggestedSpots,
        Tabs,
        ScheduleNow,
        CpInfo,
        SaveToKickstarter,
        InvoiceDates,
        InvoiceStatus,
        InvoiceFile,
        Description,
        Amount
    } from './TableComponents'

    import store from '../../../../store'

    export default {
        name: 'Card',
        props: {
            columns: Array,
            data: Array,
            totalCount: Number,
            loading: Boolean,
            setExpandedData: Function,
            expanded: Boolean,
            noFilter: Boolean,
            noFooter: Boolean
        },
        components: {
            Icon,
            Tabs,
            Table,
            Campaign,
            SuggestedSpots,
            Status,
            UpdatesStatus,
            UpdateSpots,
            Actions,
            Date,
            RowCount,
            Pagination,
            ChatIcon,
            ScheduleNow,
            CpInfo,
            SaveToKickstarter,
            EmptyState,
            Button,
            Filters,
            Loader,
            InvoiceDates,
            InvoiceStatus,
            InvoiceFile,
            Description,
            Amount
        },
        setup(props, {root}){
            const {route, router} = useRouter();
            const tabSection = computed(() => route.value.query.type)
            const { emptyState } = useEmptyState()
            const showFilter = ref(false)

            watch(route, route => {
                if(route.query.id){
                    showFilter.value = true
                }
            }, {immediate: true}) 

            const toggleFilter = () => {
                showFilter.value = !showFilter.value
            }

            return {
                tabSection,
                emptyState,
                showFilter,
                DEFAULT_PER_PAGE,
                toggleFilter
            }
        }
    }
</script>

<style lang="scss" scoped>
@import '../../../../assets/scss/variables.scss';

div.request-card {
    background: #FFFFFF;
    box-shadow: 0px 2px 8px rgba(8, 35, 48, 0.08), 0px 1px 2px rgba(8, 35, 48, 0.06);
    border-radius: 8px;
    // max-height: 742px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    margin-bottom: 50px;

    div.request-card-header {
        border-radius: 8px;
        background: $c11;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-right: 24px;
    }
    // div.request-card-body {
    //     max-height: 610px;
    //     overflow-y: auto;
    // }
    div.request-card-footer {
        height: 78px !important;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin-top: auto;
        padding-right: 32px;
        border-top: 1px solid $c28;
    }
    .warning {
        color: $c5;
    }
}
</style>